import { useState } from 'react';
import { useApi } from '../lib/use-api';
import { api, relativeTime } from '../lib/api';
import { Empty, ErrorBanner, Loading, Panel, Status } from '../components/ui';

interface Release {
  id: string;
  version: string;
  release_notes: string | null;
  is_published: boolean;
  is_withdrawn: boolean;
  devices_running: number;
  created_at: string;
}

interface Rollout {
  id: string;
  name: string;
  version: string;
  status: string;
  max_concurrent: number;
  window_start: string | null;
  created_at: string;
}

interface RolloutDetail {
  rollout: Rollout & { is_withdrawn: boolean };
  progress: Record<string, number>;
}

/** Wireframe C5 — firmware releases and staged rollouts (FR-C17, FR-C18). */
export function Firmware() {
  const releases = useApi<{ items: Release[] }>('/internal/firmware/releases');
  const rollouts = useApi<{ items: Rollout[] }>('/internal/firmware/rollouts');
  const [selected, setSelected] = useState<string | null>(null);
  const detail = useApi<RolloutDetail>(selected ? `/internal/firmware/rollouts/${selected}` : null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function act(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.post(path, body);
      releases.reload();
      rollouts.reload();
      detail.reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Firmware</h1>
          <p>Staged rollout with a concurrency cap, so a bad version is caught after a few devices, not the fleet.</p>
        </div>
      </div>

      <ErrorBanner error={error ?? releases.error} />

      <Panel title="Releases">
        {releases.loading ? (
          <Loading />
        ) : releases.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Version</th>
                <th>Notes</th>
                <th className="num">Devices running</th>
                <th>State</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.data.items.map((release) => (
                <tr key={release.id}>
                  <td className="mono">{release.version}</td>
                  <td className="muted">{release.release_notes ?? '—'}</td>
                  <td className="num">{release.devices_running}</td>
                  <td>
                    {release.is_withdrawn ? (
                      <span className="badge danger">withdrawn</span>
                    ) : release.is_published ? (
                      <span className="badge ok">published</span>
                    ) : (
                      <span className="badge">draft</span>
                    )}
                  </td>
                  <td>
                    <div className="actions">
                      {!release.is_published && !release.is_withdrawn ? (
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={() => act(`/internal/firmware/releases/${release.id}/publish`)}
                        >
                          Publish
                        </button>
                      ) : null}
                      {!release.is_withdrawn ? (
                        <button
                          className="danger"
                          disabled={busy}
                          onClick={() => {
                            const reason = window.prompt('Why is this version being withdrawn?');
                            if (reason) act(`/internal/firmware/releases/${release.id}/withdraw`, { reason });
                          }}
                        >
                          Withdraw
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No firmware releases registered.</Empty>
        )}
      </Panel>

      <Panel title="Rollouts">
        {rollouts.data?.items.length ? (
          <table>
            <thead>
              <tr>
                <th>Rollout</th>
                <th>Version</th>
                <th>Status</th>
                <th className="num">Wave size</th>
                <th>Scheduled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rollouts.data.items.map((rollout) => (
                <tr key={rollout.id}>
                  <td>
                    <button
                      className="secondary"
                      style={{ padding: '3px 8px' }}
                      onClick={() => setSelected(selected === rollout.id ? null : rollout.id)}
                    >
                      {rollout.name}
                    </button>
                  </td>
                  <td className="mono">{rollout.version}</td>
                  <td>
                    <Status value={rollout.status} />
                  </td>
                  <td className="num">{rollout.max_concurrent}</td>
                  <td className="muted">
                    {rollout.window_start ? `from ${new Date(rollout.window_start).toLocaleString()}` : 'immediate'}
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        disabled={busy || ['completed', 'rolled_back'].includes(rollout.status)}
                        onClick={() => act(`/internal/firmware/rollouts/${rollout.id}/advance`)}
                      >
                        Push next wave
                      </button>
                      <button
                        className="secondary"
                        disabled={busy || rollout.status === 'paused'}
                        onClick={() => {
                          const reason = window.prompt('Why pause this rollout?');
                          if (reason) act(`/internal/firmware/rollouts/${rollout.id}/pause`, { reason });
                        }}
                      >
                        Pause
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No rollouts created.</Empty>
        )}
      </Panel>

      {selected && detail.data ? (
        <Panel title={`Rollout progress — ${detail.data.rollout.name}`}>
          <div className="panel-body">
            <RolloutProgress progress={detail.data.progress} />
            <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
              Updates are applied without interrupting live guest sessions (FR-C18, NFR-V2). Created{' '}
              {relativeTime(detail.data.rollout.created_at)}.
            </p>
          </div>
        </Panel>
      ) : null}
    </>
  );
}

function RolloutProgress({ progress }: { progress: Record<string, number> }) {
  const total = Object.values(progress).reduce((sum, count) => sum + count, 0);
  const applied = progress.applied ?? 0;
  const percent = total > 0 ? Math.round((applied / total) * 100) : 0;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
        <span>
          {applied} / {total} updated
        </span>
        <span className="muted">{percent}%</span>
      </div>
      <div className="progress">
        <div style={{ width: `${percent}%` }} />
      </div>
      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {Object.entries(progress).map(([state, count]) => (
          <span key={state} className="badge">
            {state.replace(/_/g, ' ')}: {count}
          </span>
        ))}
      </div>
    </>
  );
}
